import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiX, FiSearch } from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import inventoryService from "../../services/inventoryService";

const TX_TYPES = ["Stock In", "Stock Out", "Return", "Adjustment"];
const ISSUED_TO = ["Student", "Department", "Other"];
const ADJ_REASONS = ["Damaged", "Lost", "Expired", "Audit Correction", "Donated", "Other"];

const TX_CONFIG = {
    "Stock In": { color: "#16a34a", bg: "#dcfce7", description: "Receiving new or restocked items from a supplier." },
    "Stock Out": { color: "#dc2626", bg: "#fee2e2", description: "Issuing items to a student or department." },
    "Return": { color: "#003b67", bg: "#e8f1f8", description: "Items returned back to the store." },
    "Adjustment": { color: "#d97706", bg: "#fef3c7", description: "Manual correction for damaged, lost, or expired items. Use negative quantity for loss." },
};

const today = () => new Date().toISOString().slice(0, 10);

export default function TransactionForm() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { academicYear } = useApp();

    const [txType, setTxType] = useState(searchParams.get("type") || "Stock In");
    const [selectedItem, setSelectedItem] = useState(null);
    const [itemSearch, setItemSearch] = useState("");
    const [itemResults, setItemResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [suppliers, setSuppliers] = useState([]);

    const [form, setForm] = useState({
        quantity: "",
        transactionDate: today(),
        supplier: "",
        invoiceNo: "",
        invoiceDate: "",
        unitPrice: "",
        issuedToType: "Department",
        issuedToName: "",
        adjustmentReason: ADJ_REASONS[0],
        remarks: "",
        recordedBy: "",
    });

    const [loading, setLoading] = useState(false);

    // Pre-select item from query param
    const presetItemId = searchParams.get("item");

    useEffect(() => {
        if (presetItemId) {
            inventoryService.getItemById(presetItemId)
                .then((res) => setSelectedItem(res.data))
                .catch(() => { });
        }
        inventoryService.getSuppliers()
            .then((r) => setSuppliers(r.data))
            .catch(() => { });
    }, [presetItemId]);

    // Item search
    useEffect(() => {
        if (!itemSearch.trim() || itemSearch.length < 2) { setItemResults([]); return; }
        const timer = setTimeout(async () => {
            setSearching(true);
            try {
                const res = await inventoryService.getItems({
                    academicYear: academicYear?._id,
                    search: itemSearch,
                    limit: 8,
                });
                setItemResults(res.data);
            } catch { setItemResults([]); }
            finally { setSearching(false); }
        }, 300);
        return () => clearTimeout(timer);
    }, [academicYear?._id, itemSearch]);

    const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));

    const handleSubmit = async () => {
        if (!selectedItem) { toast.error("Select an item."); return; }
        if (!form.quantity) { toast.error("Quantity is required."); return; }
        if (Number(form.quantity) < 1) { toast.error("Quantity must be at least 1."); return; }

        setLoading(true);
        try {
            const payload = {
                item: selectedItem._id,
                academicYear: academicYear?._id,
                type: txType,
                quantity: Number(form.quantity),
                transactionDate: form.transactionDate,
                supplier: form.supplier || null,
                invoiceNo: form.invoiceNo,
                invoiceDate: form.invoiceDate,
                unitPrice: Number(form.unitPrice) || 0,
                issuedToType: txType === "Stock Out" || txType === "Return" ? form.issuedToType : "",
                issuedToName: txType === "Stock Out" || txType === "Return" ? form.issuedToName : "",
                adjustmentReason: txType === "Adjustment" ? form.adjustmentReason : "",
                remarks: form.remarks,
                recordedBy: form.recordedBy,
            };

            const res = await inventoryService.createTransaction(payload);
            toast.success(res.message);
            navigate(`/inventory/items/${selectedItem._id}`);
        } catch (err) { toast.error(err.message); }
        finally { setLoading(false); }
    };

    const tc = TX_CONFIG[txType] || TX_CONFIG["Stock In"];

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">New Transaction</h1>
                    <p className="page-subtitle">Record a stock movement</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/inventory")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}
                        style={{ background: tc.color, borderColor: tc.color }}>
                        <FiSave size={14} /> {loading ? "Saving…" : "Save Transaction"}
                    </button>
                </div>
            </div>

            {/* Type selector */}
            <div className="card" style={{ marginBottom: "1rem" }}>
                <div className="card-header">Transaction Type</div>
                <div className="card-body">
                    <div style={{ display: "flex", gap: "0.65rem", flexWrap: "wrap" }}>
                        {TX_TYPES.map((t) => {
                            const c = TX_CONFIG[t];
                            const active = txType === t;
                            return (
                                <button key={t} type="button" onClick={() => setTxType(t)}
                                    style={{
                                        padding: "0.65rem 1.25rem", borderRadius: "var(--radius)", border: "none",
                                        cursor: "pointer", fontWeight: 700, fontSize: 13.5,
                                        background: active ? c.color : "#f3f4f6",
                                        color: active ? "#fff" : "#6b7280",
                                        outline: active ? `2px solid ${c.color}` : "none",
                                        outlineOffset: 2,
                                    }}>
                                    {t}
                                </button>
                            );
                        })}
                    </div>
                    <div style={{ marginTop: "0.75rem", fontSize: 13, color: tc.color, background: tc.bg, padding: "0.5rem 0.85rem", borderRadius: 6 }}>
                        {tc.description}
                    </div>
                </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem" }}>

                {/* ── Item Selection ── */}
                <div className="card">
                    <div className="card-header">Select Item *</div>
                    <div className="card-body">
                        {selectedItem ? (
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "0.75rem" }}>
                                <div>
                                    <div style={{ fontWeight: 700 }}>{selectedItem.name}</div>
                                    <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
                                        {selectedItem.category?.name} · Current stock: <strong style={{ color: "#16a34a" }}>{selectedItem.currentStock} {selectedItem.unit}</strong>
                                    </div>
                                    {selectedItem.location && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>📍 {selectedItem.location}</div>}
                                </div>
                                <button className="btn btn-outline btn-sm" onClick={() => { setSelectedItem(null); setItemSearch(""); }}>
                                    Change
                                </button>
                            </div>
                        ) : (
                            <div>
                                <div className="search-bar" style={{ width: "100%", marginBottom: "0.5rem" }}>
                                    <FiSearch size={14} color="#9ca3af" />
                                    <input placeholder="Search item by name or code…" value={itemSearch} onChange={(e) => setItemSearch(e.target.value)} autoFocus />
                                </div>
                                {searching && <div style={{ fontSize: 12.5, color: "var(--text-muted)", padding: "0.5rem 0" }}>Searching…</div>}
                                {itemResults.length > 0 && (
                                    <div style={{ border: "1px solid var(--border)", borderRadius: "var(--radius)", overflow: "hidden" }}>
                                        {itemResults.map((item) => (
                                            <div key={item._id} onClick={() => { setSelectedItem(item); setItemSearch(""); setItemResults([]); }}
                                                style={{ padding: "0.6rem 0.85rem", cursor: "pointer", borderBottom: "1px solid var(--border)", fontSize: 13 }}
                                                onMouseEnter={(e) => { e.currentTarget.style.background = "#f0f9ff"; }}
                                                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                                                <div style={{ fontWeight: 600 }}>{item.name}</div>
                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                    {item.category?.name} · Stock: {item.currentStock} {item.unit}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Transaction Details ── */}
                <div className="card">
                    <div className="card-header">Transaction Details</div>
                    <div className="card-body">
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Quantity * {selectedItem && <span style={{ color: "var(--text-muted)", fontSize: 11 }}>({selectedItem.unit})</span>}</label>
                                <input type="number" min="1" className="form-control" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 10" autoFocus={!!selectedItem} />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Transaction Date *</label>
                                <input type="date" className="form-control" value={form.transactionDate} onChange={set("transactionDate")} />
                            </div>

                            {/* Stock In fields */}
                            {txType === "Stock In" && (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">Supplier</label>
                                        <select className="form-control" value={form.supplier} onChange={set("supplier")}>
                                            <option value="">— Select supplier —</option>
                                            {suppliers.map((s) => <option key={s._id} value={s._id}>{s.name}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Invoice No.</label>
                                        <input className="form-control" value={form.invoiceNo} onChange={set("invoiceNo")} placeholder="e.g. INV-2025-001" />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Invoice Date</label>
                                        <input type="date" className="form-control" value={form.invoiceDate} onChange={set("invoiceDate")} />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">Unit Price (₹)</label>
                                        <input type="number" min="0" step="0.01" className="form-control" value={form.unitPrice} onChange={set("unitPrice")} placeholder="0" />
                                    </div>
                                </>
                            )}

                            {/* Stock Out / Return fields */}
                            {(txType === "Stock Out" || txType === "Return") && (
                                <>
                                    <div className="form-group">
                                        <label className="form-label">Issued To</label>
                                        <select className="form-control" value={form.issuedToType} onChange={set("issuedToType")}>
                                            {ISSUED_TO.map((t) => <option key={t} value={t}>{t}</option>)}
                                        </select>
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label">
                                            {form.issuedToType === "Student" ? "Student Name" : "Department / Name"}
                                        </label>
                                        <input className="form-control" value={form.issuedToName} onChange={set("issuedToName")}
                                            placeholder={form.issuedToType === "Student" ? "Student name" : "e.g. Pharmacology Lab"} />
                                    </div>
                                </>
                            )}

                            {/* Adjustment fields */}
                            {txType === "Adjustment" && (
                                <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                    <label className="form-label">Reason</label>
                                    <select className="form-control" value={form.adjustmentReason} onChange={set("adjustmentReason")}>
                                        {ADJ_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    <span className="form-hint">For losses (damaged/lost/expired), quantity will be deducted from stock.</span>
                                </div>
                            )}

                            {/* Common */}
                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Remarks</label>
                                <textarea className="form-control" rows={2} style={{ resize: "vertical" }} value={form.remarks} onChange={set("remarks")} placeholder="Optional notes…" />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Recorded By</label>
                                <input className="form-control" value={form.recordedBy} onChange={set("recordedBy")} placeholder="Your name (optional)" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}